import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import Mark from './components/Mark';
import LanguageSelector from './components/LanguageSelector';
import { useI18n } from './i18n';
import { colors, font, type, layout } from './theme/tokens';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: font.sans,
    fontSize: '15px',
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: layout.radius,
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.borderStrong },
    '&.Mui-focused fieldset': { borderColor: colors.ink, borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': { padding: '14px 14px' },
  '& input::placeholder': { color: colors.inkGhost, opacity: 1 },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 100px ${colors.bg} inset`,
    WebkitTextFillColor: colors.ink,
  },
} as const;

const labelSx = {
  ...type.eyebrow,
  fontSize: '10px',
  color: colors.inkFaint,
  mb: 0.75,
  display: 'block',
} as const;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const from = (location.state as any)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password || loading) return;
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(t('login.error'));
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: colors.bgAlt,
      fontFamily: font.sans,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── Topbar ──────────────────────────────────────────────────────── */}
      <Box sx={{
        height: layout.topbarHeight,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.barTint,
        display: 'flex',
        alignItems: 'center',
        px: layout.gutter,
        justifyContent: 'space-between',
      }}>
        <Mark />
        <LanguageSelector />
      </Box>

      {/* ── Centre ──────────────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 6,
      }}>
        <Box sx={{ width: '100%', maxWidth: '400px' }}>

          <Box sx={{ width: 48, height: 5, backgroundColor: colors.accent, mb: 3 }} />

          <Typography sx={{ ...type.eyebrow, color: colors.inkFaint }}>
            {t('login.eyebrow')}
          </Typography>

          <Typography sx={{
            ...type.display,
            fontSize: '34px',
            color: colors.ink,
            mt: 1.5,
            mb: 1,
          }}>
            {t('login.title')}
          </Typography>

          <Typography sx={{
            ...type.body,
            fontSize: '15px',
            color: colors.inkMuted,
            mb: 4,
          }}>
            {t('login.subtitle')}
          </Typography>

          {/* Card */}
          <Box sx={{
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: layout.radiusLg,
            p: { xs: 3, sm: 4 },
          }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography component="label" htmlFor="login-email" sx={labelSx}>
                {t('login.email')}
              </Typography>
              <TextField
                id="login-email"
                fullWidth
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="email"
                autoFocus
                variant="outlined"
                sx={fieldSx}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor="login-password" sx={labelSx}>
                {t('login.password')}
              </Typography>
              <TextField
                id="login-password"
                fullWidth
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="current-password"
                variant="outlined"
                sx={fieldSx}
              />
            </Box>

            {error && (
              <Box sx={{
                mt: 2.5,
                px: 1.75,
                py: 1.25,
                backgroundColor: colors.alertSoft,
                borderLeft: `3px solid ${colors.alert}`,
                borderRadius: `0 ${layout.radius} ${layout.radius} 0`,
              }}>
                <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.alert }}>
                  {error}
                </Typography>
              </Box>
            )}

            <Button
              fullWidth
              onClick={handleSubmit}
              disabled={loading || !email || !password}
              disableRipple
              sx={{
                mt: 3.5,
                py: 1.5,
                backgroundColor: colors.accent,
                color: colors.ink,
                fontFamily: font.sans,
                fontWeight: 700,
                fontSize: '14px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                borderRadius: layout.radiusPill,
                boxShadow: 'none',
                '&:hover': { backgroundColor: colors.accentPress, boxShadow: 'none' },
                '&:disabled': { backgroundColor: colors.border, color: colors.inkGhost },
              }}
            >
              {loading ? <CircularProgress size={19} sx={{ color: colors.ink }} /> : t('login.submit')}
            </Button>
          </Box>

          <Typography sx={{
            ...type.meta,
            color: colors.inkGhost,
            mt: 4,
            letterSpacing: '0.12em',
          }}>
            OPEN CAPACITY · LAB ENVIRONMENT
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
